import { useState, useEffect } from 'react';
import { Button, Table, Modal, Form, Badge, Alert, Spinner } from 'react-bootstrap';
import { ApiService } from '../services/api';
import { components, paths } from '../types';

type PATUser = components['schemas']['PATUser'];
type PATCreateUserRequest = paths['/api/pat/cognito/users']['post']['requestBody']['content']['application/json'];
type PATUpdateUserRequest = paths['/api/pat/cognito/users/{username}']['put']['requestBody']['content']['application/json'];

const AVAILABLE_GROUPS = [
  { name: 'PlatformAdmins', description: 'Full access to all platform features' },
  { name: 'PlatformOperators', description: 'Limited operational access' },
  { name: 'PlatformReadOnly', description: 'Read-only access' },
];

export function UserManagement() {
  const [users, setUsers] = useState<PATUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<PATUser | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    givenName: '',
    familyName: '',
    groups: [] as string[],
    sendEmail: true,
  });
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{
    title: string;
    message: string;
    action: () => void;
    variant: 'danger' | 'warning';
  } | null>(null);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [groupModalUser, setGroupModalUser] = useState<PATUser | null>(null);
  const [userGroups, setUserGroups] = useState<string[]>([]);
  const [currentUser, setCurrentUser] = useState<{ username: string; email: string } | null>(null);

  useEffect(() => {
    loadUsers();
    loadCurrentUser();
  }, []);

  const loadCurrentUser = async () => {
    try {
      const user = await ApiService.getCurrentUser();
      setCurrentUser(user);
    } catch (err) {
      console.error('Failed to load current user:', err);
      // Don't show error to user, just log it
    }
  };

  const isCurrentUser = (username: string): boolean => {
    if (!currentUser) return false;
    return currentUser.username === username || currentUser.email === username;
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await ApiService.listPATUsers();
      setUsers(response.users || []);
    } catch (err: any) {
      console.error('Failed to load users:', err);
      setError(err?.message || err?.code || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    try {
      setActionLoading('create');
      setError(null);
      
      // Create user with groups (API requires groups field)
      const createRequest: PATCreateUserRequest = {
        username: formData.email,
        email: formData.email,
        givenName: formData.givenName,
        familyName: formData.familyName,
        groups: formData.groups as ("PlatformAdmins" | "PlatformOperators" | "PlatformReadOnly")[],
        sendWelcomeEmail: formData.sendEmail,
      };
      
      await ApiService.createPATUser(createRequest);
      
      setSuccessMessage(`User ${formData.email} created successfully!`);
      setShowCreateModal(false);
      resetForm();
      loadUsers();
    } catch (err: any) {
      console.error('Failed to create user:', err);
      setError(err?.message || err?.code || 'Failed to create user');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser?.username) return;
    
    try {
      setActionLoading('update');
      setError(null);
      
      // Update basic user info (without groups)
      const updateRequest: PATUpdateUserRequest = {
        givenName: formData.givenName,
        familyName: formData.familyName,
      };
      
      await ApiService.updatePATUser(selectedUser.username, updateRequest);
      
      // Handle group changes separately using individual API calls
      const currentGroups = selectedUser.groups || [];
      const newGroups = formData.groups;
      
      // Groups to add
      const groupsToAdd = newGroups.filter(group => !currentGroups.includes(group));
      for (const group of groupsToAdd) {
        await ApiService.addPATUserToGroup(selectedUser.username, group);
      }
      
      // Groups to remove
      const groupsToRemove = currentGroups.filter(group => !newGroups.includes(group));
      for (const group of groupsToRemove) {
        await ApiService.removePATUserFromGroup(selectedUser.username, group);
      }
      
      setSuccessMessage(`User ${selectedUser.username} updated successfully!`);
      setShowEditModal(false);
      setSelectedUser(null);
      resetForm();
      loadUsers();
    } catch (err: any) {
      console.error('Failed to update user:', err);
      setError(err?.message || err?.code || 'Failed to update user');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDisableUser = async (username: string) => {
    try {
      setActionLoading(`disable-${username}`);
      setError(null);
      
      await ApiService.disablePATUser(username);
      setSuccessMessage(`User ${username} disabled successfully!`);
      loadUsers();
    } catch (err: any) {
      console.error('Failed to disable user:', err);
      setError(err?.message || err?.code || 'Failed to disable user');
    } finally {
      setActionLoading(null);
    }
  };

  const handleEnableUser = async (username: string) => {
    try {
      setActionLoading(`enable-${username}`);
      setError(null);
      
      await ApiService.enablePATUser(username);
      setSuccessMessage(`User ${username} enabled successfully!`);
      loadUsers();
    } catch (err: any) {
      console.error('Failed to enable user:', err);
      setError(err?.message || err?.code || 'Failed to enable user');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteUser = async (username: string) => {
    try {
      setActionLoading(`delete-${username}`);
      setError(null);
      
      await ApiService.deletePATUser(username);
      setSuccessMessage(`User ${username} deleted successfully!`);
      loadUsers();
    } catch (err: any) {
      console.error('Failed to delete user:', err);
      setError(err?.message || err?.code || 'Failed to delete user');
    } finally {
      setActionLoading(null);
    }
  };

  /**
   * Show a confirmation modal for destructive actions
   * @param title - Modal title
   * @param message - Confirmation message
   * @param action - Function to execute if confirmed
   * @param variant - Modal style ('danger' for delete, 'warning' for disable)
   */
  const showConfirmation = (title: string, message: string, action: () => void, variant: 'danger' | 'warning' = 'danger') => {
    setConfirmAction({
      title,
      message,
      action: () => {
        setShowConfirmModal(false);
        action();
      },
      variant
    });
    setShowConfirmModal(true);
  };

  const confirmDisableUser = (username: string) => {
    showConfirmation(
      'Disable User',
      `Are you sure you want to disable user "${username}"? They will no longer be able to access the platform.`,
      () => handleDisableUser(username),
      'warning'
    );
  };

  const confirmEnableUser = (username: string) => {
    showConfirmation(
      'Enable User',
      `Are you sure you want to enable user "${username}"? They will be able to access the platform again.`,
      () => handleEnableUser(username),
      'warning'
    );
  };

  const confirmDeleteUser = (username: string) => {
    showConfirmation(
      'Delete User',
      `Are you sure you want to permanently delete user "${username}"? This action cannot be undone and will remove all user data.`,
      () => handleDeleteUser(username),
      'danger'
    );
  };

  const openGroupModal = async (user: PATUser) => {
    if (!user.username) return;
    
    try {
      setGroupModalUser(user);
      setUserGroups(user.groups || []);
      setShowGroupModal(true);
    } catch (err: any) {
      console.error('Failed to load user groups:', err);
      setError(err?.message || err?.code || 'Failed to load user groups');
    }
  };

  const handleToggleUserGroup = async (groupName: string) => {
    if (!groupModalUser?.username) return;
    
    try {
      setActionLoading(`group-${groupName}`);
      setError(null);
      
      const isInGroup = userGroups.includes(groupName);
      
      if (isInGroup) {
        await ApiService.removePATUserFromGroup(groupModalUser.username, groupName);
        setUserGroups(prev => prev.filter(g => g !== groupName));
        setSuccessMessage(`Removed ${groupModalUser.username} from ${groupName}`);
      } else {
        await ApiService.addPATUserToGroup(groupModalUser.username, groupName);
        setUserGroups(prev => [...prev, groupName]);
        setSuccessMessage(`Added ${groupModalUser.username} to ${groupName}`);
      }
      
      // Refresh the main user list to show updated groups
      loadUsers();
    } catch (err: any) {
      console.error('Failed to toggle user group:', err);
      setError(err?.message || err?.code || 'Failed to update user group');
    } finally {
      setActionLoading(null);
    }
  };

  const openEditModal = async (user: PATUser) => {
    if (!user.username) return;
    
    try {
      setActionLoading(`loading-${user.username}`);
      setError(null);
      
      // Fetch the latest user groups to ensure we have current data
      const userGroupsResponse = await ApiService.listPATUserGroups(user.username);
      const currentGroups = userGroupsResponse.groups?.map((g: any) => g.groupName) || user.groups || [];
      
      setSelectedUser(user);
      setFormData({
        email: user.email || '',
        givenName: user.givenName || '',
        familyName: user.familyName || '',
        groups: currentGroups,
        sendEmail: false,
      });
      setShowEditModal(true);
    } catch (err: any) {
      console.error('Failed to load user details:', err);
      setError(err?.message || err?.code || 'Failed to load user details');
      // Fallback to cached data if API call fails
      setSelectedUser(user);
      setFormData({
        email: user.email || '',
        givenName: user.givenName || '',
        familyName: user.familyName || '',
        groups: user.groups || [],
        sendEmail: false,
      });
      setShowEditModal(true);
    } finally {
      setActionLoading(null);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      givenName: '',
      familyName: '',
      groups: [],
      sendEmail: true,
    });
  };

  const toggleGroup = (groupName: string) => {
    setFormData(prev => ({
      ...prev,
      groups: prev.groups.includes(groupName)
        ? prev.groups.filter(g => g !== groupName)
        : [...prev.groups, groupName]
    }));
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
        <p className="mt-3">Loading users...</p>
      </div>
    );
  }

  return (
    <div>
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert variant="success" dismissible onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      <div className="card">
        <div className="card-header">
          <div className="d-flex justify-content-between align-items-center">
            <h3 className="card-title">PAT Users</h3>
            <div>
              <Button 
                variant="outline-secondary" 
                size="sm" 
                className="me-2"
                onClick={loadUsers}
                disabled={loading}
              >
                <i className="fas fa-sync-alt"></i>
                {loading ? ' Loading...' : ' Refresh'}
              </Button>
              <Button variant="primary" size="sm" onClick={() => setShowCreateModal(true)}>
                <i className="fas fa-plus me-2"></i>
                Create User
              </Button>
            </div>
          </div>
        </div>
        <div className="card-body p-0">
          <Table striped hover responsive className="mb-0">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Status</th>
                <th>Groups</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-4 text-muted">
                    <i className="fas fa-users fa-2x mb-3"></i>
                    <br />
                    No PAT users found. Create your first user to get started.
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.username}>
                    <td>{user.givenName} {user.familyName}</td>
                    <td>{user.email}</td>
                    <td>
                      <Badge bg={user.enabled ? 'success' : 'secondary'}>
                        {user.enabled ? 'Active' : 'Disabled'}
                      </Badge>
                      {' '}
                      <Badge bg={user.status === 'CONFIRMED' ? 'info' : 'warning'}>
                        {user.status}
                      </Badge>
                    </td>
                    <td>
                      {user.groups && user.groups.length > 0 ? (
                        user.groups.map(group => (
                          <Badge key={group} bg="primary" className="me-1">
                            {group}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-muted">No groups</span>
                      )}
                    </td>
                    <td>
                      {user.createdDate ? new Date(user.createdDate).toLocaleDateString() : 'N/A'}
                    </td>
                    <td>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-1"
                        onClick={() => openEditModal(user)}
                        disabled={actionLoading === 'update' || actionLoading === `loading-${user.username}`}
                        title="Edit user"
                      >
                        {actionLoading === `loading-${user.username}` ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          <i className="fas fa-edit"></i>
                        )}
                      </Button>
                      <Button
                        variant="outline-info"
                        size="sm"
                        className="me-1"
                        onClick={() => openGroupModal(user)}
                        title="Manage groups"
                      >
                        <i className="fas fa-users"></i>
                      </Button>
                      {user.username && (
                        <>
                          {user.enabled ? (
                            <Button
                              variant="outline-warning"
                              size="sm"
                              className="me-1"
                              onClick={() => confirmDisableUser(user.username!)}
                              disabled={actionLoading === `disable-${user.username}`}
                              title="Disable user"
                            >
                              {actionLoading === `disable-${user.username}` ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                <i className="fas fa-ban"></i>
                              )}
                            </Button>
                          ) : (
                            <Button
                              variant="outline-success"
                              size="sm"
                              className="me-1"
                              onClick={() => confirmEnableUser(user.username!)}
                              disabled={actionLoading === `enable-${user.username}`}
                              title="Enable user"
                            >
                              {actionLoading === `enable-${user.username}` ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                <i className="fas fa-check"></i>
                              )}
                            </Button>
                          )}
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => confirmDeleteUser(user.username!)}
                            disabled={
                              actionLoading === `delete-${user.username}` || 
                              isCurrentUser(user.username!)
                            }
                            title={
                              isCurrentUser(user.username!) 
                                ? "Cannot delete your own account" 
                                : "Delete user permanently"
                            }
                          >
                            {actionLoading === `delete-${user.username}` ? (
                              <Spinner animation="border" size="sm" />
                            ) : (
                              <i className="fas fa-trash"></i>
                            )}
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      </div>

      {/* Create User Modal */}
      <Modal show={showCreateModal} onHide={() => { setShowCreateModal(false); resetForm(); }}>
        <Modal.Header closeButton>
          <Modal.Title>Create New User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Email Address *</Form.Label>
              <Form.Control
                type="email"
                placeholder="user@nationalrail.co.uk"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
              <Form.Text className="text-muted">
                This will be used as the username
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>First Name *</Form.Label>
              <Form.Control
                type="text"
                placeholder="John"
                value={formData.givenName}
                onChange={(e) => setFormData({ ...formData, givenName: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Last Name *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Doe"
                value={formData.familyName}
                onChange={(e) => setFormData({ ...formData, familyName: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Groups</Form.Label>
              {AVAILABLE_GROUPS.map(group => (
                <Form.Check
                  key={group.name}
                  type="checkbox"
                  id={`create-group-${group.name}`}
                  label={
                    <>
                      <strong>{group.name}</strong>
                      <br />
                      <small className="text-muted">{group.description}</small>
                    </>
                  }
                  checked={formData.groups.includes(group.name)}
                  onChange={() => toggleGroup(group.name)}
                  className="mb-2"
                />
              ))}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="sendEmail"
                label="Send welcome email with temporary password"
                checked={formData.sendEmail}
                onChange={(e) => setFormData({ ...formData, sendEmail: e.target.checked })}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowCreateModal(false); resetForm(); }}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateUser}
            disabled={!formData.email || !formData.givenName || !formData.familyName || actionLoading === 'create'}
          >
            {actionLoading === 'create' ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Creating...
              </>
            ) : (
              'Create User'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit User Modal */}
      <Modal show={showEditModal} onHide={() => { setShowEditModal(false); setSelectedUser(null); resetForm(); }}>
        <Modal.Header closeButton>
          <Modal.Title>Edit User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser && (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Email Address</Form.Label>
                <Form.Control
                  type="email"
                  value={formData.email}
                  disabled
                />
                <Form.Text className="text-muted">
                  Email cannot be changed
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>First Name *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.givenName}
                  onChange={(e) => setFormData({ ...formData, givenName: e.target.value })}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Last Name *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.familyName}
                  onChange={(e) => setFormData({ ...formData, familyName: e.target.value })}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Groups</Form.Label>
                {AVAILABLE_GROUPS.map(group => (
                  <Form.Check
                    key={group.name}
                    type="checkbox"
                    id={`edit-group-${group.name}`}
                    label={
                      <>
                        <strong>{group.name}</strong>
                        <br />
                        <small className="text-muted">{group.description}</small>
                      </>
                    }
                    checked={formData.groups.includes(group.name)}
                    onChange={() => toggleGroup(group.name)}
                    className="mb-2"
                  />
                ))}
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowEditModal(false); setSelectedUser(null); resetForm(); }}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUpdateUser}
            disabled={!formData.givenName || !formData.familyName || actionLoading === 'update'}
          >
            {actionLoading === 'update' ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Confirmation Modal */}
      <Modal 
        show={showConfirmModal} 
        onHide={() => setShowConfirmModal(false)}
        centered
        size="sm"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="w-100 text-center">
            <div className={`rounded-circle d-inline-flex align-items-center justify-content-center mb-2 ${
              confirmAction?.variant === 'danger' ? 'bg-danger-subtle' : 'bg-warning-subtle'
            }`} style={{ width: '48px', height: '48px' }}>
              <i className={`fas ${
                confirmAction?.variant === 'danger' ? 'fa-exclamation-triangle text-danger' : 'fa-exclamation-circle text-warning'
              } fa-lg`}></i>
            </div>
            <div className="h5 mb-0">{confirmAction?.title}</div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center pt-0">
          <p className="text-muted mb-0">{confirmAction?.message}</p>
        </Modal.Body>
        <Modal.Footer className="border-0 justify-content-center">
          <Button 
            variant="outline-secondary" 
            onClick={() => setShowConfirmModal(false)}
            className="me-2"
          >
            Cancel
          </Button>
          <Button 
            variant={confirmAction?.variant || 'danger'} 
            onClick={confirmAction?.action}
          >
            {confirmAction?.title === 'Enable User' ? 'Enable' : 
             confirmAction?.variant === 'danger' ? 'Delete' : 'Disable'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Group Management Modal */}
      <Modal 
        show={showGroupModal} 
        onHide={() => { setShowGroupModal(false); setGroupModalUser(null); setUserGroups([]); }}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Manage Groups - {groupModalUser?.givenName} {groupModalUser?.familyName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {groupModalUser && (
            <>
              <p className="text-muted mb-4">
                <strong>User:</strong> {groupModalUser.email}
              </p>
              
              <h6 className="mb-3">Available Groups</h6>
              {AVAILABLE_GROUPS.map(group => {
                const isInGroup = userGroups.includes(group.name);
                const isLoading = actionLoading === `group-${group.name}`;
                
                return (
                  <div key={group.name} className="d-flex align-items-center justify-content-between p-3 border rounded mb-2">
                    <div>
                      <div className="fw-bold">{group.name}</div>
                      <small className="text-muted">{group.description}</small>
                    </div>
                    <div className="d-flex align-items-center">
                      {isInGroup && (
                        <Badge bg="success" className="me-2">
                          <i className="fas fa-check me-1"></i>
                          Member
                        </Badge>
                      )}
                      <Button
                        variant={isInGroup ? "outline-danger" : "outline-success"}
                        size="sm"
                        onClick={() => handleToggleUserGroup(group.name)}
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          <>
                            <i className={`fas ${isInGroup ? 'fa-minus' : 'fa-plus'} me-1`}></i>
                            {isInGroup ? 'Remove' : 'Add'}
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => { setShowGroupModal(false); setGroupModalUser(null); setUserGroups([]); }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
